import { Component, Host, h, State, Watch, Listen, Prop, Method } from '@stencil/core';
import { ApiService } from '../../services/api';
import { Location, LocationArea, LocationType } from '../../interfaces/location';
import {
  arrayOfObjectsDeepSort,
  getDistanceFromLatLng,
  getGoolgeMapsDirectionsLink,
} from '../../utils/utils';

declare var window;

@Component({
  tag: 'location-search',
  styleUrl: 'location-search.scss',
  scoped: true,
})
/**
 * @todo add _Specialties_ to filters
 */
export class LocationSearch {
  /**
   * Reference to Map Element
   */
  private gMap: HTMLLocationMapElement = null;

  /**
   * Reference to Map Modal
   */
  private mapModal: HTMLSimpleModalElement = null;
  /**
   * Reference to Modal
   */
  private filterModal: HTMLSimpleModalElement = null;
  /**
   * Locations List
   */
  private locationsListElement: HTMLElement = null;
  /**
   * Toggle Map Button
   */
  private toggleMapButtonElement: HTMLInputElement;

  /** 
   * constructor to add hash change listener
   */
  constructor() {
    this.hashChangeListener = this.hashChangeListener.bind(this);
  }

  connectedCallback() {
    window.addEventListener('hashchange', this.hashChangeListener);
  }

  disconnectedCallback() {
    window.removeEventListener('hashchange', this.hashChangeListener);
  }

  hashChangeListener(event: HashChangeEvent) {
    this.findHashForFiltering();
    this.filterLocations();
  }
  /**
   * Full list of Locations
   */
  @State() locations: Location[] = null;

  /**
   * All data loaded indicator
   */
  @State() dataLoaded: boolean = false;
  @Watch('locations')
  markDataLoaded(newValue, oldValue) {
    if (this.locations.length && !this.dataLoaded) {
      this.dataLoaded = true;
      /// Prevent screen readers jumping to map
      setTimeout(() => this.locationsListElement.focus(), 250);
    }
  }

  /**
   * Location Areas - used for 'near me' filtering
   */
  @State() areas: LocationArea[] = null;
  /**
   * Location Types - used for tabbed filtering
   */
  @State() locationTypes: LocationType[] = null;
  /**
   * ID of currently selected location type
   */
  @State() selectedLocationTypeId: string = null;
  @Watch('selectedAdditionalFilters')
  updateSelectedAdditionalFilters() {
    if (!this.selectedAdditionalFilters.LocationTypes.length) {
      this.selectedLocationTypeId = null;
    } else {
      this.selectedLocationTypeId = this.selectedAdditionalFilters.LocationTypes[0];
    }
  }
  /**
   * ID of currently selected area
   */
  @State() selectedAreaId: string = null;
  /**
   * Selected Additional Filters - object of arrays of IDs
   */
  @State() additionalFilters: { [key: string]: any[] } = {};
  @State() selectedAdditionalFilters: { [key: string]: string[] } = {};
  @State() selectedAdditionalFiltersOperators: { [key: string]: 'OR' | 'AND' };
  @State() selectedAdditionalFiltersCount: number = 0;
  @Watch('selectedAdditionalFilters')
  watchSelectedAdditionalFilters(newValue) {
    this.selectedAdditionalFiltersCount = Object.keys(newValue).reduce((count, key) => {
      count += newValue[key].length;
      return count;
    }, 0);
  }
  /**
   * Sort order
   */
  @State() sortAscending: boolean = true;
  @Watch('sortAscending')
  watchSortAscending() {
    this.filterLocations();
  }
  /**
   * Requesting Location
   */
  @State() geolocating: boolean = false;
  /**
   * User geolocation
   */
  @State() userGeolocation: GeolocationPosition = null;
  /**
   * Sort by Geolocate - requires user consent
   */
  @State() sortByGeolocation: boolean = false;
  /**
   * Location set with filters applied
   */
  @State() filteredLocations: Location[] = null;
  @Watch('filteredLocations')
  watchFilteredLocations() {
    if (this.gMap) {
      this.gMap.locations = this.filteredLocations as any;
    }
  }

  /** Type Ahead */
  /**
   * Search term to filter by
   */
  @State() searchTerm: string = '';
  /**
   * Location suggestions for type-ahead
   */
  @State() suggestedLocations: { text: string; value: Location }[] = [];
  /**
   * No suggestions indicator
   */
  @State() noSuggestionMatches: boolean = false;

  /** Map */
  @State() activeLocationId: string = null;

  /** Device Info */
  private desktopMinWidth: number = 768;
  /** On mobile device */
  @State() onDevice: boolean = false;

  @Listen('resize', { target: 'window', passive: true, capture: true })
  handleResize() {
    let onDevice = window.innerWidth < this.desktopMinWidth;
    if (this.onDevice !== onDevice) {
      this.onDevice = onDevice;
    }
  }

  @Listen('scroll', { target: 'window' })
  onScroll() {
    let scrollPosition = window.pageYOffset;
    let el = document.getElementById('filters-container');
    console.log(scrollPosition);
    if (scrollPosition > 122) {
      let headerHeight = document.getElementById('navigation').offsetHeight;

      el.style.paddingTop = '122px';
    } else {
      el.style.paddingTop = '0';
    }
  }

  handleToggleMap() {
    let headerHeight = document.getElementById('navigation').offsetHeight + 'px';
    console.log(headerHeight);
    let map = document.getElementById('location-map');

    if (map.classList.contains('show')) {
      map.classList.remove('show');
      document.body.style.overflow = 'visible';
      this.toggleMapButtonElement.innerHTML = "Show Map <icon-map-pin class='btn-icon' width='20px' height='20px'></icon-map-pin>";
    } else {
      map.classList.add('show');
      document.body.style.overflow = 'hidden';
      map.style.top = headerHeight;
      this.toggleMapButtonElement.innerHTML = "Show List <icon-list class='btn-icon ms-1' width='18' height='21px'></icon-list>";
    }

  }
  /**
   * Clear Additional Filters
   */
  @Method() async clearAdditionalFilters() {
    console.log('clearing');
    this.setupAdditionalFilters();
    this.filterLocations();
  }

  componentWillLoad() {
    this.handleResize();
    window.localStorage.removeItem("adena_locations_v2");
    ApiService.getLocations().then(locations => {
      this.locations = locations;
      // Loading all locations, so we can deduce the full sets of areas
      // and locationTypes from the locations response
      this.setupAdditionalFilters();
      this.setupAreas(locations);
      this.setupLocationTypes(locations);


      this.findHashForFiltering();

      this.filterLocations();
    });
  }

  private findHashForFiltering() {
    let url = new URL(window.location);
    if (url.hash) {
      var found: LocationType = this.locationTypes.find(value => value.UrlName === url.hash.substring(1));
      if (found) {
        this.selectedLocationTypeId = String(found.Id);
        this.updateAdditionalFilters('LocationTypes', found.Id, true);
      }
    }
  }
  /**
   * Grab all areas from Locations
   */
  private setupAreas(locations: Location[]) {
    const areas = locations.reduce((areas, location) => {
      location.Areas.forEach(area => (areas[area.Id] = area));
      return areas;
    }, {});
    this.areas = (Object.values(areas) as LocationArea[]).sort(
      this.sortApiDataByTitle.bind(this),
    );
    this.additionalFilters.Areas = this.areas;
  }

  /**
   * Grab all locationTypes from Locations
   */
  private setupLocationTypes(locations: Location[]) {
    const locationTypes = locations.reduce((types, location) => {
      location.LocationTypes.forEach(type => (types[type.Id] = type));
      return types;
    }, {});
    this.locationTypes = Object.values(locationTypes);
    this.additionalFilters.LocationTypes = this.locationTypes;
  }

  /**
   * Setup Additional Filters Model
   */
  private setupAdditionalFilters() {
    this.selectedAdditionalFilters = { LocationTypes: [], Areas: [], Specialties: [] };
    this.selectedAdditionalFiltersOperators = {
      LocationTypes: 'AND',
      Areas: 'OR',
      Specialties: 'OR',
    };
  }

  /**
   * Update Additional Filters
   */
  private updateAdditionalFilters(key: string, value: string, replace?: boolean) {
    let filters = this.selectedAdditionalFilters[key],
      index = filters.indexOf(value);
    if (index > -1) {
      filters.splice(index, 1);
    } else {
      if (replace) {
        filters = [value];
      } else {
        filters.push(value);
      }
    }
    this.selectedAdditionalFilters = {
      ...this.selectedAdditionalFilters,
      [key]: filters,
    };
    this.filterLocations();
  }



  /**
   * Results filtering logic
   */
  private filterLocations() {
    let filteredLocations = this.locations;
    if (this.selectedAreaId) {
      filteredLocations = filteredLocations.filter(this.locationHasArea.bind(this));
    }
    if (this.selectedAdditionalFiltersCount) {
      filteredLocations = filteredLocations.filter(
        this.locationHasAdditionalFilters.bind(this),
      );
    }
    if ('' !== this.searchTerm) {
      // do additional filtering
      filteredLocations = filteredLocations.filter((location, index) =>
        this.locationHasSearchTerm(location, index, this.searchTerm),
      );
    }
    if (this.sortByGeolocation) {
      // do geolocation sort
      filteredLocations = this.computeAndOrderLocationsByDistance(filteredLocations);
      console.log('Geolocated', filteredLocations);
    } else {
      filteredLocations = filteredLocations.sort(this.sortApiDataByTitle.bind(this));
      if (!this.sortAscending) {
        filteredLocations = filteredLocations.reverse();
      }
    }
    this.filteredLocations = filteredLocations;
    // console.log(this.locations);
  }

  private computeAndOrderLocationsByDistance(locations: Location[]): Location[] {
    return locations
      .map(location => this.computeDistanceForLocation(location))
      .sort(this.sortLocationsByDistanceToUser.bind(this));
  }

  private computeDistanceForLocation(location: Location): Location {
    location.DistanceToUserMiles = getDistanceFromLatLng(
      location.Latitude,
      location.Longitude,
      this.userGeolocation.coords.latitude,
      this.userGeolocation.coords.longitude,
      true,
    );
    return location;
  }

  private locationHasArea(location: Location, index: number): boolean {
    return location.Areas.some(area => area.Id === this.selectedAreaId);
  }

  private locationHasLocationType(location: Location, index: number): boolean {
    return location.LocationTypes.some(type => type.Id === this.selectedLocationTypeId);
  }

  private locationHasAdditionalFilters(location: Location, index: number): boolean {
    for (let key in this.selectedAdditionalFilters) {
      if (!this.selectedAdditionalFilters[key].length) {
        continue;
      }
      for (let i = 0; i < this.selectedAdditionalFilters[key].length; i++) {
        let value = this.selectedAdditionalFilters[key][i];
        if ('AND' == this.selectedAdditionalFiltersOperators[key]) {
          if (!location[key].some(d => d.Id === value)) {
            return false;
          }
        } else if ('OR' == this.selectedAdditionalFiltersOperators[key]) {
          if (location[key].some(d => d.Id === value)) {
            return true;
          }
        }
      }
      if ('OR' == this.selectedAdditionalFiltersOperators[key]) {
        // No match
        return false;
      }
    }
    return true;
  }

  private locationHasSearchTerm(location: Location, index: number, term?: string): boolean {
    let parts = (term ? term : this.searchTerm).toLowerCase().split(' ');
    /**
     * @todo add `searchString` property for each location on page load
     */
    let searchString = (
      location.Title +
      location.City +
      location.State +
      location.Zip
    ).toLowerCase();
    for (let i = 0; i < parts.length; i++) {
      if (searchString.indexOf(parts[i]) === -1) {
        return false;
      }
    }
    return true;
  }

  private sortApiDataByTitle(a: Location | LocationType, b: Location | LocationType): number {
    return arrayOfObjectsDeepSort(a, b, ['Title']);
  }

  private sortLocationsByDistanceToUser(a: Location, b: Location): number {
    return arrayOfObjectsDeepSort(a, b, ['DistanceToUserMiles']);
  }

  private orderByGeolocation() {
    if (null !== this.userGeolocation) {
      this.sortByGeolocation = true;
      this.filterLocations();
    } else {
      this.geolocating = true;
      navigator.geolocation.getCurrentPosition(
        position => {
          this.geolocating = false;
          this.userGeolocation = position;
          this.sortByGeolocation = true;
          this.filterLocations();
        },
        err => {
          this.geolocating = false;
          console.error(err);
        },
      );
    }
  }

  private suggestLocations(event) {
    const searchTerm = event.detail;
    let suggestions = [];
    if (searchTerm.length && searchTerm.replace(/\ /g, '').length > 0) {
      /**
       * @devnote should this search the filtered results, or
       * all locations?
       */
      suggestions = this.locations
        .filter((location, index) =>
          this.locationHasSearchTerm(location, index, searchTerm),
        )
        .map(this.makeTypeAheadSuggestion.bind(this));
      this.noSuggestionMatches = !suggestions.length;
    } else {
      this.noSuggestionMatches = false;
    }
    this.suggestedLocations = suggestions;
  }

  private makeTypeAheadSuggestion(location: Location): { text: string; value: Location } {
    return {
      text: location.Title,
      value: location,
    };
  }

  private getPopupHtmlForLocation(location: Location) {
    let imgSrc = 'placeholderImageSrc',
      href = '#';
    if (location.PageUrl.length) {
      href = location.PageUrl[0];
    }
    if (location.Image.length) {
      imgSrc = location.Image[0].ThumbnailUrl;
    }
    return `
            <div class="location-popup">
                <div class="location-popup-image">
					<a href="${href}">
						<img src="${imgSrc}"/>
					</a>
                </div>
                <div class="location-popup-details">
                    <h3><a href="${href}">${location.Title}</a></h3>
                    <p>
						${location.Address || 'No Address'},<br />
						${location.City || 'No City'}, ${location.State || 'No State'} ${location.Zip} 
						<a href="${getGoolgeMapsDirectionsLink(location)}"" target="_blank">
							(map)
						</a>
					</p>
                    <p>
                        <a href="${'tel:' + location.Phone.replace(/\-/gi, "")}" target="_blank">
                            ${location.Phone}
                        </a>
                    </p>
                </div>
            </div>
        `;
  }

  addPopupToMap = async (location: Location, marker: google.maps.Marker = null) => {
    await this.gMap.showInfoWindow(
      this.getPopupHtmlForLocation(location),
      new google.maps.LatLng(location.Latitude, location.Longitude),
    );
  };

  handleMapMarkerClick = (event: CustomEvent) => {
    const { location, marker } = event.detail;
    this.addPopupToMap(location, marker);
  };

  handleLocationClick = (event: Event, location: Location) => {
    //event.preventDefault();
    // Let's zoom and focus location instead
    // this.map.locations = [location];
    if (null === location.Latitude || null === location.Longitude) {
      return;
    }
    this.gMap.center = { latitude: location.Latitude, longitude: location.Longitude };
    this.gMap.zoom = 20;
    this.addPopupToMap(location);
  };

  handleTabClick = event => {
    const { data } = event.detail.item;
    this.selectedLocationTypeId = this.selectedLocationTypeId === data.Id ? null : data.Id;
    this.updateAdditionalFilters('LocationTypes', data.Id, true);
  };

  handleTypeAheadSuggestionClick(event) {
    // alert(`Go to detail view for location: ${event.detail.Title}`);
    let location = event.detail as Location;
    if (location.PageUrl.length) {
      window.location = location.PageUrl[0];
    } else {
      this.filteredLocations = [event.detail];
      this.suggestedLocations = [];
    }
  }

  handleApplyAdditionalFilters(event) {
    const filters = event.detail;
    this.selectedAdditionalFilters = {
      ...filters,
    };
    // this.filterModal.show = false;
    this.filterLocations();
  }

  startWaitTime(clinic_id) {
    //60000
    if (clinic_id == "") return;

    const selector = `#current-wait-${clinic_id}`;

    this.updateWaitTime(clinic_id, selector);

    setTimeout(() => {
      this.updateWaitTime(clinic_id, selector);
    }, 60000);
  }

  updateWaitTime(clinic_id, selector) {
    var api = "https://api.clockwisemd.com/v1/hospitals/" + clinic_id + "/waits";
    fetch(api).then(response => response.json()).then(data => {
      console.log(data);
      var item: HTMLElement = this.locationsListElement.querySelector(selector);
      item.innerHTML = data.hospital_waits.current_wait;
    });
  }

  additionalFiltersHTML() {
    return (
      <div>
        <button class="button-filters" onClick={() => (this.filterModal.show = true)}>
          Filters &bull; {this.selectedAdditionalFiltersCount}
        </button>
        {this.selectedAdditionalFiltersCount > 0 && (
          <a
            href="#clearFilters"
            class="clear"
            onClick={event => {
              event.preventDefault();
              this.clearAdditionalFilters();
            }}
          >
            Clear
          </a>
        )}
      </div>
    );
  }

  locationDetailHTML(location: Location) {
    //let locationType = this.locationType(location);
    return (
      <div class="location-details">
        <div class="location-detail address">
          <p>
            {location.Address || 'No Address'},<br />
            {location.City || 'No City'}, {location.State || 'No State'} {location.Zip}
            <br />
            <a href={getGoolgeMapsDirectionsLink(location)} target="_blank">
              Get directions
            </a>
          </p>
        </div>
        <div class="location-detail hours">
          <p>
            {/* {location.Hours || 'No location hours'} */}
            General Contact
            <br />
            {!!location.Phone && (
              <a href={'tel:' + location.Phone} target="_blank">
                {location.Phone}
              </a>
            )}
          </p>
        </div>
        {/* {location.ClockwiseMDClinicId != "0" && location.ClockwiseMDClinicId != "" && location.ClockwiseMDClinicId != null && (
          <clockwise-waittime clinic_id={location.ClockwiseMDClinicId}></clockwise-waittime>
        )} */}
        
        {location.EPICWaitTimeId != "0" && location.EPICWaitTimeId != "" && location.EPICWaitTimeId != null && (
          <epic-wait-times
            clinic_id={location.EPICWaitTimeId}
            clinic_type={location.EPICWaitTimeType}
            clinic_title={location.Title}></epic-wait-times>
        )}
        {location.ScheduleLink != "" && location.ScheduleLink != null && (
          <div class="location-schedule-link-container">
            <p>Urgent Care</p>
            <a href={location.ScheduleLink} target='_blank' class="btn btn-brand-green">Schedule an Appointment</a>
          </div>
        )}

        {/* {!!location.PageUrl.length && (
          <div class="location-detail link">
            <a href={location.PageUrl[0]}>
              <span>View location details</span>
              <icon-arrow />
            </a>
          </div>
        )} */}
      </div>
    );
  }
  // locationType(location) {
  // 	var urgentCare = location.LocationTypes.some(e=>e.UrlName === "urgent-care");
  // 	var emergency = location.LocationTypes.some(e=>e.UrlName === "emergency-care");
  // 	//if (urgentCare) return "ImmediateCareWait";
  // 	if (emergency) return "EDWait";
  // 	return null;
  // }

  orderingOptionsHTML() {
    return (
      <div class="results-ordering sub-header right">
        <a
          href="#ascending"
          class={{ active: this.sortAscending && !this.sortByGeolocation }}
          onClick={event => {
            event.preventDefault();
            this.sortByGeolocation = false;
            this.sortAscending = true;
          }}
        >
          A-Z
        </a>
        <a
          href="#descending"
          id="descending"
          class={{ active: !this.sortAscending && !this.sortByGeolocation }}
          onClick={event => {
            event.preventDefault();
            this.sortByGeolocation = false;
            this.sortAscending = false;
          }}
        >
          Z-A
        </a>
        <a
          href="#geolocation"
          class={{ active: this.sortByGeolocation }}
          onClick={event => {
            event.preventDefault();
            this.orderByGeolocation();
          }}
        >
          Near me {this.geolocating}
        </a>
      </div>
    );
  }

  resultsCountHTML() {
    return (
      <span class="results-count text-light sub-header left" aria-live="polite">
        {this.filteredLocations && this.filteredLocations.length} locations found
      </span>
    );
  }

  searchItemHeadingHTML(location: Location) {
    return (
      <span>
        <span>{location.Title}</span>
        {this.sortByGeolocation && (
          <span class="distance">{location.DistanceToUserMiles.toFixed(2)} mi.</span>
        )}
      </span>
    );
  }

  locationsListHeaderHTML() {
    return (
      <div class="container-fluid header-container">

        <header class="flex">
          <div class="container">
            <div class="row align-items-center h-100">
              <div class="col-12 col-md-3">
                <h1>Locations</h1>
              </div>
              <div class="col-12 col-md-9">
                <type-ahead
                  placeholder="Search"
                  suggestions={this.suggestedLocations}
                  noSuggestions={this.noSuggestionMatches}
                  onDoSearch={event => {
                    this.searchTerm = event.detail;
                    this.suggestedLocations = [];
                    this.filterLocations();
                  }}
                  onTextEntered={event => {
                    this.searchTerm = '';
                    this.suggestLocations(event);
                  }}
                  onTextCleared={event => {
                    this.searchTerm = '';
                    this.filterLocations();
                  }}
                  onSuggestionSelected={event => {
                    this.searchTerm = '';
                    this.handleTypeAheadSuggestionClick(event);
                  }}
                ></type-ahead>
              </div>
              <div class="location-list-sorting">
                {/* {this.orderingOptionsHTML()} */}
                {/* {this.additionalFiltersHTML()} */}
              </div>
            </div>
          </div>
        </header >
      </div >
    );
  }

  locationsListBodyHTML() {
    return (
      <div ref={el => (this.locationsListElement = el)} class="location-list-results">
        {this.filteredLocations &&
          this.filteredLocations.map(location => (
            <search-result-item
              accessibleTitle={location.Title}
              url={location.PageUrl.length ? location.PageUrl[0] : null}
              heading={this.searchItemHeadingHTML(location)}
              image={
                // @todo add helper for this
                location.Image && location.Image.length
                  ? {
                    src: location.Image[0].Url,
                    alt: location.Image[0].AlternativeText,
                  }
                  : null
              }
              onMouseEnter={() => (this.activeLocationId = location.Id)}
              // onMouseLeave={() => (this.activeLocationId = null)}
              onClick={event => this.handleLocationClick(event, location)}
            >
              {this.locationDetailHTML(location)}
            </search-result-item>
          ))}
      </div>
    );
  }
  locationsListHeaderHtml() {
    <div class="location-list-search-header">
      {this.locationsListHeaderHTML()}
    </div>
  }
  locationsListHTML() {
    return this.dataLoaded ? (
      <div class="location-list h-100">
        {this.onDevice && this.locationMapHTML()}
        {/* {this.onDevice && this.resultsCountHTML()} */}
        {this.locationsListBodyHTML()}
      </div>
    ) : (
      <loading-indicator text="Loading location data"></loading-indicator>
    );
  }

  locationMapHTML() {
    return (
      <location-map
        id='location-map'
        ref={el => (this.gMap = el as HTMLLocationMapElement)}
        onLoaded={() => {
          this.gMap.locations = this.filteredLocations;
        }}
        activeLocationIdentifier="Id"
        activeLocation={this.activeLocationId}
        onMarkerClicked={this.handleMapMarkerClick}
      ></location-map>
    );
  }

  renderMapButtonHTML() {
    return (
      <button class="btn btn-show-map" onClick={() => (this.handleToggleMap())} ref={(el) => this.toggleMapButtonElement = el as HTMLInputElement}>Show Map <icon-map-pin class='btn-icon' width='20px' height='20px'></icon-map-pin></button>
    )
  }

  render() {
    return (
      <Host>
        {/* {!this.onDevice && this.locationTypes && (
					<tab-list
						onTabClick={event => this.handleTabClick(event)}
						items={this.locationTypes.sort(this.sortApiDataByTitle).map(item => ({
							text: item.Title,
							data: item,
							active: this.selectedLocationTypeId === item.Id,
						}))}
					></tab-list>
				)} */}
        <div
          // @todo revisit appropriate settings
          // aria-live="polite"
          // aria-busy={this.dataLoaded ? 'false' : 'true'}
          class={{ 'locations': true, 'locations--device': this.onDevice }}
        >
          {this.locationsListHeaderHTML()}
          {/* <simple-modal ref={el => (this.filterModal = el)}> */}
          <div class="container-fluid position-relative">

            <div class="container h-100">
              <div class="row h-100">
                <div class="col-12 col-md-3">
                  <div id='filters-container' class="sticky">
                    <location-filters
                      class="sticky"
                      filterGroups={this.additionalFilters}
                      filterGroupTitles={{
                        LocationTypes: 'Locations Type',
                        Areas: 'Areas',
                        Specialties: 'Specialties',
                      }}
                      selectedFilterIds={this.selectedAdditionalFilters}
                      onApplyFilters={event => this.handleApplyAdditionalFilters(event)}
                    ></location-filters>
                  </div>
                </div>
                {/* </simple-modal> */}
                <div class="col-12 col-md-9">
                  <div class="location-list-header">
                    {!this.onDevice && this.resultsCountHTML()}
                    {!this.onDevice && this.orderingOptionsHTML()}
                  </div>
                  {this.locationsListHTML()}
                </div>
                {!this.onDevice && this.locationMapHTML()}
              </div>
            </div>
          </div>
          {!this.onDevice && this.renderMapButtonHTML()}
        </div>
      </Host>
    );
  }
}
